/**
 * @zendir/ui - ZenSpace3D Component (Optimized)
 *
 * Professional 3D space visualization with smooth interactions.
 * Optimized for performance with settings from EarthViewer.
 */

import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useMemo,
} from "react";
import { useTheme } from "../theme";
import { AstroIcon, type AstroIconName } from "../core/display/AstroIcon";
import type { GroundStation as _GroundStation } from "../types";
import type {
  ZenSpace3DProps,
  ZenSpace3DHandle,
  ViewMode,
  SpaceObject,
  Vector3D,
  CameraState,
  LayerVisibility,
  SelectionState,
  OverpassInfo,
} from "./ZenSpace3DTypes";
import {
  latLonAltToCartesian,
  SCENE_EARTH_RADIUS,
  KM_TO_SCENE,
  easeOutCubic,
} from "./ZenSpace3DUtils";
import { formatDuration } from "../utils";

// Import shaders as raw text (like EarthViewer)
import atmosphereVertexShader from "../../shaders/atmosphere.vert";
import atmosphereFragmentShader from "../../shaders/atmosphere.frag";
import starsVertexShader from "../../shaders/stars.vert";
import starsFragmentShader from "../../shaders/stars.frag";
import { loadThree, ThreeModule } from "./threeLoader";
import { ZenSpace3DCesium } from "./ZenSpace3DCesium";

// =============================================================================
// Communication Link Types
// =============================================================================

export interface CommunicationLink {
  id: string;
  fromId: string;
  toId: string;
  type: "uplink" | "downlink" | "crosslink" | "relay";
  status: "active" | "idle" | "blocked";
  color?: string;
  bandwidth?: number;
  latency?: number;
  signalStrength?: number;
}

// =============================================================================
// Configuration - Optimized for smooth interactions
// =============================================================================

const DEFAULT_LAYERS: LayerVisibility = {
  planets: true,
  moons: true,
  satellites: true,
  debris: false,
  stations: true,
  asteroids: false,
  groundStations: true,
  orbits: true,
  labels: true,
  constellations: false,
  coverage: true,
  atmosphere: true,
  stars: true,
  grid: false,
  terminator: true,
};

const CAMERA_TRANSITION_DURATION = 2000;
const STARS_COUNT = 5000; // Reduced from 8000
const EARTH_SEGMENTS = 64; // Reduced from 128
const EARTH_SEGMENTS_V = 32; // Reduced from 64
const SPHERICAL_SUBDIVISIONS = 16; // Subdivisions for curved edges on coverage

function checkWebGLCapabilities(): { available: boolean; version: number } {
  try {
    const canvas = document.createElement("canvas");
    const gl2 = canvas.getContext("webgl2");
    if (gl2) return { available: true, version: 2 };
    const gl = canvas.getContext("webgl");
    if (gl) return { available: true, version: 1 };
  } catch {
    /* WebGL not available */
  }
  return { available: false, version: 0 };
}

// =============================================================================
// Spherical Geometry Helpers - For realistic curved coverage on sphere
// =============================================================================

/**
 * Spherical linear interpolation (SLERP) between two unit vectors
 * Creates smooth arc along the sphere surface
 */
function slerp(
  v1: { x: number; y: number; z: number },
  v2: { x: number; y: number; z: number },
  t: number,
): { x: number; y: number; z: number } {
  // Normalize inputs
  const len1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y + v1.z * v1.z);
  const len2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y + v2.z * v2.z);

  // Handle zero-length vectors
  if (len1 < 0.0001 || len2 < 0.0001) {
    return { x: v1.x, y: v1.y, z: v1.z };
  }

  const n1 = { x: v1.x / len1, y: v1.y / len1, z: v1.z / len1 };
  const n2 = { x: v2.x / len2, y: v2.y / len2, z: v2.z / len2 };

  // Calculate angle between vectors
  let dot = n1.x * n2.x + n1.y * n2.y + n1.z * n2.z;
  dot = Math.max(-1, Math.min(1, dot)); // Clamp for numerical stability
  const theta = Math.acos(dot);

  if (theta < 0.001) {
    // Vectors are nearly parallel, use linear interpolation
    return {
      x: n1.x + t * (n2.x - n1.x),
      y: n1.y + t * (n2.y - n1.y),
      z: n1.z + t * (n2.z - n1.z),
    };
  }

  const sinTheta = Math.sin(theta);
  if (Math.abs(sinTheta) < 0.0001) {
    // Near zero sin, use linear interpolation
    return {
      x: n1.x + t * (n2.x - n1.x),
      y: n1.y + t * (n2.y - n1.y),
      z: n1.z + t * (n2.z - n1.z),
    };
  }

  const a = Math.sin((1 - t) * theta) / sinTheta;
  const b = Math.sin(t * theta) / sinTheta;

  return {
    x: a * n1.x + b * n2.x,
    y: a * n1.y + b * n2.y,
    z: a * n1.z + b * n2.z,
  };
}

/**
 * Subdivide polygon edges along great circles for spherical rendering
 * Takes corner points and returns a densely sampled spherical polygon
 */
function subdivideSphericalPolygon(
  corners: Array<{ x: number; y: number; z: number }>,
  subdivisions: number,
  radius: number,
): Array<{ x: number; y: number; z: number }> {
  if (corners.length < 3 || subdivisions < 1 || radius <= 0) {
    return corners; // Return as-is if invalid params
  }

  const result: Array<{ x: number; y: number; z: number }> = [];

  for (let i = 0; i < corners.length; i++) {
    const p1 = corners[i];
    const p2 = corners[(i + 1) % corners.length];

    // Validate points
    if (
      !isFinite(p1.x) ||
      !isFinite(p1.y) ||
      !isFinite(p1.z) ||
      !isFinite(p2.x) ||
      !isFinite(p2.y) ||
      !isFinite(p2.z)
    ) {
      continue; // Skip invalid points
    }

    // Add interpolated points along the great circle arc
    for (let j = 0; j < subdivisions; j++) {
      const t = j / subdivisions;
      const interpolated = slerp(p1, p2, t);

      // Validate interpolation result
      if (
        !isFinite(interpolated.x) ||
        !isFinite(interpolated.y) ||
        !isFinite(interpolated.z)
      ) {
        continue;
      }

      // Project onto sphere surface at given radius
      const len = Math.sqrt(
        interpolated.x ** 2 + interpolated.y ** 2 + interpolated.z ** 2,
      );
      if (len < 0.0001) continue; // Skip zero-length vectors

      result.push({
        x: (interpolated.x / len) * radius,
        y: (interpolated.y / len) * radius,
        z: (interpolated.z / len) * radius,
      });
    }
  }

  return result.length > 0 ? result : corners; // Fallback to original if all failed
}

// =============================================================================
// Satellite Shape Builders (simplified for performance)
// =============================================================================

function createSatelliteShape(
  THREE: ThreeModule,
  category: string,
  size: number,
): any {
  const group = new THREE.Group();

  if (category === "station") {
    // Simple station shape
    const geo = new THREE.BoxGeometry(size * 1.2, size * 0.6, size * 0.6);
    const mat = new THREE.MeshBasicMaterial({ color: 0xcccccc });
    group.add(new THREE.Mesh(geo, mat));

    // Solar panels
    const panelGeo = new THREE.BoxGeometry(size * 2.5, size * 0.03, size * 0.6);
    const panelMat = new THREE.MeshBasicMaterial({ color: 0x1a4a7a });
    const leftPanel = new THREE.Mesh(panelGeo, panelMat);
    leftPanel.position.x = -size * 1.5;
    group.add(leftPanel);
    const rightPanel = new THREE.Mesh(panelGeo, panelMat);
    rightPanel.position.x = size * 1.5;
    group.add(rightPanel);
  } else if (category === "satellite") {
    // Simple satellite
    const geo = new THREE.BoxGeometry(size * 0.5, size * 0.5, size * 0.6);
    const mat = new THREE.MeshBasicMaterial({ color: 0xaaaaaa });
    group.add(new THREE.Mesh(geo, mat));

    const panelGeo = new THREE.BoxGeometry(size * 1.2, size * 0.02, size * 0.4);
    const panelMat = new THREE.MeshBasicMaterial({ color: 0x1a3a6a });
    const leftPanel = new THREE.Mesh(panelGeo, panelMat);
    leftPanel.position.x = -size * 0.7;
    group.add(leftPanel);
    const rightPanel = new THREE.Mesh(panelGeo, panelMat);
    rightPanel.position.x = size * 0.7;
    group.add(rightPanel);
  } else if (category === "debris") {
    const geo = new THREE.IcosahedronGeometry(size * 0.3, 0);
    const mat = new THREE.MeshBasicMaterial({ color: 0x666666 });
    group.add(new THREE.Mesh(geo, mat));
  } else {
    const geo = new THREE.SphereGeometry(size * 0.4, 8, 6);
    const mat = new THREE.MeshBasicMaterial({ color: 0x888888 });
    group.add(new THREE.Mesh(geo, mat));
  }

  return group;
}

// =============================================================================
// Component
// =============================================================================

export const ZenSpace3D = forwardRef<ZenSpace3DHandle, ZenSpace3DProps>(
  function ZenSpace3D(props, ref) {
    // Prefer Cesium when available (default 3D backend); fall back to Three.js
    const [cesiumModule, setCesiumModule] = useState<unknown>(null);
    useEffect(() => {
      // Cesium is optional peer; fallback to Three.js when not installed
      import("cesium")
        .then((m) => setCesiumModule(m))
        .catch(() => setCesiumModule(null));
    }, []);
    if (cesiumModule) {
      return <ZenSpace3DCesium ref={ref} cesium={cesiumModule} {...props} />;
    }
    return <ZenSpace3DThree ref={ref} {...props} />;
  },
);

// Three.js implementation — separate component so hook count is consistent (avoids React #300)
const ZenSpace3DThree = forwardRef<ZenSpace3DHandle, ZenSpace3DProps>(
  function ZenSpace3DThree(props, ref) {
    const {
      satellites = [],
      debris = [],
      stations = [],
      groundStations = [],
      customObjects = [],
      view = "earth-orbit",
      focusedObjectId,
      initialCamera,
      sunDirection,
      planetPositions,
      visibilityCones = [],
      satelliteCoverages = [],
      overpasses: _overpasses = [],
      orbitPaths = [],
      orbitDesignArcs: _orbitDesignArcs = [],
      communicationLinks = [],
      showAxisHelper = false,
      layers: layerOverrides,
      showTools = true,
      showInfoPanel = true,
      width = "100%",
      height = 600,
      callbacks,
      maxObjects = 10000,
      className = "",
      style,
    } = props;

    const { tokens: _tokens } = useTheme();
    const [webglCapabilities] = useState(() => checkWebGLCapabilities());

    // Refs
    const containerRef = useRef<HTMLDivElement>(null);
    const rendererRef = useRef<any>(null);
    const sceneRef = useRef<any>(null);
    const cameraRef = useRef<any>(null);
    const controlsRef = useRef<any>(null);
    const animationRef = useRef<number | null>(null);
    const raycasterRef = useRef<any>(null);
    const mouseRef = useRef<any>(null);

    const earthRef = useRef<any>(null);
    const starsMaterialRef = useRef<any>(null);
    const atmosphereMaterialRef = useRef<any>(null);
    const objectsGroupRef = useRef<any>(null);
    const linksGroupRef = useRef<any>(null);
    const conesGroupRef = useRef<any>(null);
    const orbitsGroupRef = useRef<any>(null);
    const planetsGroupRef = useRef<any>(null);

    const timeRef = useRef(0);
    const focusTransitionRef = useRef<any>(null);
    const isUserInteractingRef = useRef(false);
    const objectMeshMapRef = useRef<Map<string, any>>(new Map());
    const isAnimatingRef = useRef(false); // Guard against multiple animation loops
    const _sceneInitializedRef = useRef(false); // Prevent duplicate initialization

    // State
    const [THREE, setTHREE] = useState<ThreeModule | null>(null);
    const threeRef = useRef<ThreeModule | null>(null);
    threeRef.current = THREE;
    const [OrbitControlsClass, setOrbitControlsClass] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [layers, setLayers] = useState<LayerVisibility>({
      ...DEFAULT_LAYERS,
      ...layerOverrides,
    });
    const [selection, setSelection] = useState<SelectionState>({
      selectedId: null,
      selectedObject: null,
      hoveredId: null,
    });
    const [hoverTooltip, setHoverTooltip] = useState<{
      visible: boolean;
      x: number;
      y: number;
      object: any;
    }>({ visible: false, x: 0, y: 0, object: null });
    const [currentView, setCurrentView] = useState<ViewMode>(view);

    // Ref for view to prevent initScene recreation (fixes flickering on view change)
    const currentViewRef = useRef<ViewMode>(view);

    const effectiveLayers = useMemo(
      () => ({ ...layers, ...layerOverrides }),
      [layers, layerOverrides],
    );

    useEffect(() => {
      setCurrentView(view);
      currentViewRef.current = view;
    }, [view]);

    // ==========================================================================
    // Load Three.js
    // ==========================================================================

    useEffect(() => {
      if (!webglCapabilities.available) {
        setIsLoading(false);
        return;
      }

      loadThree()
        .then(({ THREE: threeModule, OrbitControls: controls }) => {
          setTHREE(threeModule);
          setOrbitControlsClass(() => controls);
        })
        .catch((err) => {
          if (import.meta.env.DEV) {
            console.error("Failed to load Three.js:", err);
          }
          setError("Failed to load 3D library");
          setIsLoading(false);
        });
    }, [webglCapabilities.available]);

    // ==========================================================================
    // Initialize Scene (Optimized like EarthViewer)
    // ==========================================================================

    const initScene = useCallback(() => {
      if (!THREE || !containerRef.current || !OrbitControlsClass) return;

      // Clean up any existing scene first (prevents duplicates)
      if (rendererRef.current) {
        isAnimatingRef.current = false;
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }
        controlsRef.current?.dispose();
        rendererRef.current.dispose();
        if (containerRef.current?.contains(rendererRef.current.domElement)) {
          containerRef.current.removeChild(rendererRef.current.domElement);
        }
      }

      const container = containerRef.current;
      const rect = container.getBoundingClientRect();
      const w = rect.width;
      const h = typeof height === "number" ? height : rect.height;

      // Scene
      const scene = new THREE.Scene();
      sceneRef.current = scene;

      // Use ref for view mode to prevent initScene recreation (fixes flickering)
      const viewMode = currentViewRef.current;

      // Camera (adjusted per view mode)
      const farClip = viewMode === "solar-system" ? 1000000 : 100000;
      const camera = new THREE.PerspectiveCamera(45, w / h, 1, farClip);
      // Solar system: position to see inner planets, Earth orbit view: standard distance
      const initialDistance = viewMode === "solar-system" ? 5000 : 10000;
      camera.position.set(0, initialDistance * 0.3, initialDistance);
      cameraRef.current = camera;

      if (initialCamera?.position) {
        camera.position.set(
          initialCamera.position.x,
          initialCamera.position.y,
          initialCamera.position.z,
        );
      }

      // Renderer (optimized settings - preserveDrawingBuffer prevents flickering)
      const renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
        powerPreference: "high-performance",
        preserveDrawingBuffer: true, // Prevents flickering from buffer swaps
      });
      renderer.setSize(w, h);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      container.appendChild(renderer.domElement);
      rendererRef.current = renderer;

      // Controls (smooth damping, adjusted per view)
      const controls = new OrbitControlsClass(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.05;
      controls.rotateSpeed = 0.5;
      controls.minDistance = viewMode === "solar-system" ? 500 : 3050; // Very close to Earth/objects
      controls.maxDistance = viewMode === "solar-system" ? 100000 : 25000;
      controls.enablePan = viewMode === "solar-system"; // Allow pan in solar system
      let interactionTimer: ReturnType<typeof setTimeout>;
      const handleControlStart = () => {
        isUserInteractingRef.current = true;
      };
      const handleControlEnd = () => {
        clearTimeout(interactionTimer);
        interactionTimer = setTimeout(() => {
          isUserInteractingRef.current = false;
        }, 3000);
      };
      controls.addEventListener("start", handleControlStart);
      controls.addEventListener("end", handleControlEnd);
      controlsRef.current = controls;

      raycasterRef.current = new THREE.Raycaster();
      mouseRef.current = new THREE.Vector2();

      // Groups
      objectsGroupRef.current = new THREE.Group();
      objectsGroupRef.current.name = "objects";
      scene.add(objectsGroupRef.current);

      linksGroupRef.current = new THREE.Group();
      linksGroupRef.current.name = "links";
      scene.add(linksGroupRef.current);

      conesGroupRef.current = new THREE.Group();
      conesGroupRef.current.name = "cones";
      scene.add(conesGroupRef.current);

      orbitsGroupRef.current = new THREE.Group();
      orbitsGroupRef.current.name = "orbits";
      scene.add(orbitsGroupRef.current);

      planetsGroupRef.current = new THREE.Group();
      planetsGroupRef.current.name = "planets";
      scene.add(planetsGroupRef.current);

      if (viewMode === "solar-system") {
        createSolarSystem(THREE, scene);
      } else {
        createEarth(THREE, scene);
        createAtmosphere(THREE, scene);
      }

      createStars(THREE, scene);
      createLighting(THREE, scene);

      if (showAxisHelper && viewMode !== "solar-system") {
        createAxisHelper(THREE, scene);
      }

      setIsLoading(false);

      return () => {
        clearTimeout(interactionTimer);
        if (animationRef.current) cancelAnimationFrame(animationRef.current);
        controls.removeEventListener("start", handleControlStart);
        controls.removeEventListener("end", handleControlEnd);
        controls.dispose();
        renderer.dispose();
        if (container.contains(renderer.domElement))
          container.removeChild(renderer.domElement);
      };
    }, [THREE, OrbitControlsClass, height, initialCamera, showAxisHelper]); // Uses currentViewRef to prevent recreation

    // ==========================================================================
    // Create Solar System (scaled for visibility)
    // ==========================================================================

    const createSolarSystem = useCallback(
      (THREE: ThreeModule, _scene: unknown) => {
        const planetsGroup = planetsGroupRef.current;
        if (!planetsGroup) return;

        // Scale factor to make solar system viewable (1 unit = ~1000 km)
        const DISTANCE_SCALE = 0.000005;
        const SIZE_SCALE = 0.01;

        // Sun (emissive glow)
        const sunRadius = 696340 * SIZE_SCALE;
        const sunGeo = new THREE.SphereGeometry(sunRadius, 32, 24);
        const sunMat = new THREE.MeshBasicMaterial({ color: 0xffdd44 });
        const sunMesh = new THREE.Mesh(sunGeo, sunMat);
        sunMesh.name = "sun";
        sunMesh.userData = {
          objectId: "sun",
          planetId: "sun",
          isPlanet: true,
          isSelectable: true,
        };
        planetsGroup.add(sunMesh);

        // Sun glow
        const glowGeo = new THREE.SphereGeometry(sunRadius * 1.3, 24, 16);
        const glowMat = new THREE.MeshBasicMaterial({
          color: 0xffaa00,
          transparent: true,
          opacity: 0.2,
        });
        planetsGroup.add(new THREE.Mesh(glowGeo, glowMat));

        // Planets with proper scaling
        const configs = [
          {
            id: "mercury",
            color: 0x8c7853,
            radius: 2439.7,
            distance: 57900000,
          },
          { id: "venus", color: 0xffc649, radius: 6051.8, distance: 108200000 },
          { id: "earth", color: 0x6b93d6, radius: 6371, distance: 149600000 },
          { id: "mars", color: 0xc1440e, radius: 3389.5, distance: 227900000 },
          {
            id: "jupiter",
            color: 0xd8ca9d,
            radius: 69911,
            distance: 778500000,
          },
          {
            id: "saturn",
            color: 0xf4d59e,
            radius: 58232,
            distance: 1434000000,
          },
          {
            id: "uranus",
            color: 0xd1e7e7,
            radius: 25362,
            distance: 2871000000,
          },
          {
            id: "neptune",
            color: 0x5b5ddf,
            radius: 24622,
            distance: 4495000000,
          },
        ];

        configs.forEach((c) => {
          // Make inner planets larger for visibility
          const visualScale = c.distance < 300000000 ? 15 : 5;
          const planetRadius = c.radius * SIZE_SCALE * visualScale;
          const geo = new THREE.SphereGeometry(planetRadius, 24, 16);
          const mat = new THREE.MeshPhongMaterial({
            color: c.color,
            shininess: 30,
          });
          const mesh = new THREE.Mesh(geo, mat);

          const orbitDist = c.distance * DISTANCE_SCALE;
          mesh.position.x = orbitDist;
          mesh.name = c.id;
          mesh.userData = {
            objectId: c.id,
            planetId: c.id,
            isPlanet: true,
            isSelectable: true,
            data: {
              id: c.id,
              name: c.id.charAt(0).toUpperCase() + c.id.slice(1),
              category: "planet",
            },
          };
          planetsGroup.add(mesh);
          objectMeshMapRef.current.set(c.id, mesh);

          // Saturn's rings
          if (c.id === "saturn") {
            const ringGeo = new THREE.RingGeometry(
              planetRadius * 1.4,
              planetRadius * 2.2,
              32,
            );
            const ringMat = new THREE.MeshBasicMaterial({
              color: 0xc9b896,
              transparent: true,
              opacity: 0.6,
              side: THREE.DoubleSide,
            });
            const ring = new THREE.Mesh(ringGeo, ringMat);
            ring.rotation.x = Math.PI / 2.5;
            ring.position.copy(mesh.position);
            planetsGroup.add(ring);
          }

          // Orbit line
          const points: any[] = [];
          for (let i = 0; i <= 64; i++) {
            const angle = (i / 64) * Math.PI * 2;
            points.push(
              new THREE.Vector3(
                orbitDist * Math.cos(angle),
                0,
                orbitDist * Math.sin(angle),
              ),
            );
          }
          const orbitGeo = new THREE.BufferGeometry().setFromPoints(points);
          const orbitMat = new THREE.LineBasicMaterial({
            color: 0x444466,
            transparent: true,
            opacity: 0.3,
          });
          planetsGroup.add(new THREE.Line(orbitGeo, orbitMat));
        });

        // Add a point light at Sun
        const sunLight = new THREE.PointLight(0xffffee, 2, 100000);
        sunLight.position.set(0, 0, 0);
        planetsGroup.add(sunLight);
      },
      [],
    );

    // ==========================================================================
    // Create Earth (optimized segments like EarthViewer)
    // ==========================================================================

    const createEarth = useCallback((THREE: ThreeModule, scene: any) => {
      const earthGeometry = new THREE.SphereGeometry(
        SCENE_EARTH_RADIUS,
        EARTH_SEGMENTS,
        EARTH_SEGMENTS_V,
      );
      const earthMaterial = new THREE.MeshPhongMaterial({
        color: 0x2244aa,
        shininess: 10,
      });

      const textureLoader = new THREE.TextureLoader();
      textureLoader.load(
        "/world.topo.jpg",
        (texture: any) => {
          texture.wrapS = THREE.RepeatWrapping;
          texture.wrapT = THREE.ClampToEdgeWrapping;
          texture.minFilter = THREE.LinearFilter;
          texture.magFilter = THREE.LinearFilter;
          earthMaterial.map = texture;
          earthMaterial.needsUpdate = true;
        },
        undefined,
        () => {},
      );

      const earth = new THREE.Mesh(earthGeometry, earthMaterial);
      earth.rotation.y = -Math.PI / 2 + Math.PI;
      earth.name = "earth";
      scene.add(earth);
      earthRef.current = earth;
    }, []);

    // ==========================================================================
    // Create Atmosphere (sun-reactive glow)
    // ==========================================================================

    const createAtmosphere = useCallback(
      (THREE: ThreeModule, scene: any) => {
        if (!effectiveLayers.atmosphere) return;

        // Default sun direction (can be updated via props)
        const defaultSunDir = new THREE.Vector3(1, 0.3, 0.5).normalize();

        const atmosphereGeometry = new THREE.SphereGeometry(
          SCENE_EARTH_RADIUS * 1.15,
          32,
          16,
        );
        const atmosphereMaterial = new THREE.ShaderMaterial({
          uniforms: {
            uSunDirection: { value: defaultSunDir },
          },
          vertexShader: atmosphereVertexShader,
          fragmentShader: atmosphereFragmentShader,
          blending: THREE.AdditiveBlending,
          side: THREE.BackSide,
          transparent: true,
          depthWrite: false,
        });
        const atmosphere = new THREE.Mesh(
          atmosphereGeometry,
          atmosphereMaterial,
        );
        atmosphere.rotation.y = -Math.PI / 2;
        atmosphere.name = "atmosphere";
        scene.add(atmosphere);

        // Store ref for dynamic updates
        atmosphereMaterialRef.current = atmosphereMaterial;
      },
      [effectiveLayers.atmosphere],
    );

    // ==========================================================================
    // Create Stars (optimized like EarthViewer)
    // ==========================================================================

    const createStars = useCallback(
      (THREE: ThreeModule, scene: any) => {
        const starsGeometry = new THREE.BufferGeometry();
        const starsDist = currentView === "solar-system" ? 50000000000 : 50000;
        const positions = new Float32Array(STARS_COUNT * 3);
        const opacities = new Float32Array(STARS_COUNT);

        for (let i = 0; i < STARS_COUNT; i++) {
          const radius = starsDist + Math.random() * starsDist;
          const theta = Math.random() * Math.PI * 2;
          const phi = Math.acos(2 * Math.random() - 1);

          positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
          positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
          positions[i * 3 + 2] = radius * Math.cos(phi);
          opacities[i] = Math.random();
        }

        starsGeometry.setAttribute(
          "position",
          new THREE.BufferAttribute(positions, 3),
        );
        starsGeometry.setAttribute(
          "opacity",
          new THREE.BufferAttribute(opacities, 1),
        );

        const starsMaterial = new THREE.ShaderMaterial({
          uniforms: { time: { value: 0 } },
          vertexShader: starsVertexShader,
          fragmentShader: starsFragmentShader,
          transparent: true,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        });

        scene.add(new THREE.Points(starsGeometry, starsMaterial));
        starsMaterialRef.current = starsMaterial;
      },
      [currentView],
    );

    // ==========================================================================
    // Create Lighting (simple like EarthViewer)
    // ==========================================================================

    const createLighting = useCallback((THREE: ThreeModule, scene: any) => {
      scene.add(new THREE.AmbientLight(0x404040, 0.6));
      const sunLight = new THREE.DirectionalLight(0xffffff, 1.2);
      sunLight.position.set(10000, 5000, 10000);
      scene.add(sunLight);
    }, []);

    // ==========================================================================
    // Create Axis Helper
    // ==========================================================================

    const createAxisHelper = useCallback((THREE: ThreeModule, scene: any) => {
      const len = SCENE_EARTH_RADIUS * 1.5;
      const group = new THREE.Group();

      [
        { color: 0xff3333, dir: [1, 0, 0] },
        { color: 0x33ff33, dir: [0, 1, 0] },
        { color: 0x3333ff, dir: [0, 0, 1] },
      ].forEach(({ color, dir }) => {
        const points = [
          new THREE.Vector3(0, 0, 0),
          new THREE.Vector3(dir[0] * len, dir[1] * len, dir[2] * len),
        ];
        const geo = new THREE.BufferGeometry().setFromPoints(points);
        const mat = new THREE.LineBasicMaterial({ color });
        group.add(new THREE.Line(geo, mat));
      });

      scene.add(group);
    }, []);

    // ==========================================================================
    // Update Objects
    // ==========================================================================

    const updateObjects = useCallback(() => {
      if (!THREE || !objectsGroupRef.current || currentView === "solar-system")
        return;

      const group = objectsGroupRef.current;
      const meshMap = objectMeshMapRef.current;

      // Clear
      while (group.children.length > 0) {
        const child = group.children[0];
        group.remove(child);
      }
      meshMap.clear();

      const allObjects: SpaceObject[] = [
        ...satellites,
        ...debris,
        ...stations,
        ...customObjects,
      ];

      allObjects.slice(0, maxObjects).forEach((obj) => {
        const categoryLayer =
          obj.category === "satellite"
            ? "satellites"
            : obj.category === "debris"
              ? "debris"
              : obj.category === "station"
                ? "stations"
                : "satellites";

        if (!effectiveLayers[categoryLayer]) return;

        let size = 50;
        if (obj.category === "station") size = 100;
        else if (obj.category === "debris") size = 25;

        const satelliteGroup = createSatelliteShape(THREE, obj.category, size);
        satelliteGroup.position.set(
          obj.position.x,
          obj.position.y,
          obj.position.z,
        );
        satelliteGroup.lookAt(0, 0, 0);
        satelliteGroup.userData = {
          objectId: obj.id,
          category: obj.category,
          data: obj,
          isSelectable: true,
        };
        satelliteGroup.name = obj.id;

        group.add(satelliteGroup);
        meshMap.set(obj.id, satelliteGroup);
      });
    }, [
      THREE,
      satellites,
      debris,
      stations,
      customObjects,
      maxObjects,
      effectiveLayers,
      currentView,
    ]);

    // ==========================================================================
    // Update Ground Stations
    // ==========================================================================

    const updateGroundStations = useCallback(() => {
      if (!THREE || !objectsGroupRef.current || currentView === "solar-system")
        return;
      if (!effectiveLayers.groundStations) return;

      groundStations.forEach((gs) => {
        const pos = latLonAltToCartesian(gs.latitude, gs.longitude, 0);

        // Simple marker
        const geo = new THREE.SphereGeometry(35, 8, 6);
        const mat = new THREE.MeshBasicMaterial({ color: 0x00ffff });
        const marker = new THREE.Mesh(geo, mat);
        marker.position.set(pos.x, pos.y, pos.z);
        marker.userData = {
          objectId: gs.id,
          category: "groundStation",
          data: gs,
          isSelectable: true,
        };

        objectsGroupRef.current.add(marker);
        objectMeshMapRef.current.set(gs.id, marker);

        // Glow ring
        const ringGeo = new THREE.RingGeometry(45, 65, 16);
        const ringMat = new THREE.MeshBasicMaterial({
          color: 0x00ffff,
          transparent: true,
          opacity: 0.4,
          side: THREE.DoubleSide,
        });
        const ring = new THREE.Mesh(ringGeo, ringMat);
        ring.position.set(pos.x, pos.y, pos.z);
        ring.lookAt(0, 0, 0);
        objectsGroupRef.current.add(ring);
      });
    }, [THREE, groundStations, effectiveLayers.groundStations, currentView]);

    // ==========================================================================
    // Update Coverage (Realistic satellite-to-ground cones + legacy region cones)
    // ==========================================================================

    const updateCoverage = useCallback(() => {
      const T = threeRef.current;
      if (!T || !conesGroupRef.current || currentView === "solar-system")
        return;

      // Clear existing
      while (conesGroupRef.current.children.length > 0) {
        conesGroupRef.current.remove(conesGroupRef.current.children[0]);
      }

      if (!effectiveLayers.coverage) return;

      // ========================================
      // Satellite Coverage Visualization (NASA-style)
      // Renders coverage data provided by Zendir SDK
      // ========================================
      satelliteCoverages.forEach((coverage) => {
        // Get satellite position
        let satPos: any;
        if (coverage.satellitePosition) {
          satPos = new T.Vector3(
            coverage.satellitePosition.x,
            coverage.satellitePosition.y,
            coverage.satellitePosition.z,
          );
        } else {
          const satMesh = objectMeshMapRef.current.get(coverage.satelliteId);
          if (!satMesh) return;
          satPos = satMesh.position.clone();
        }

        const satDistance = satPos.length();
        const coneColor = new T.Color(coverage.color || "#22d3ee");
        const highlightColor = new T.Color(
          coverage.color || "#22d3ee",
        ).multiplyScalar(1.3);

        // ====== 1. Render footprint border only (no filled plane - it cuts through sphere) ======
        if (
          coverage.footprintPolygon &&
          coverage.footprintPolygon.length >= 3 &&
          coverage.showFootprint !== false
        ) {
          const cornerPoints = coverage.footprintPolygon.map((pt) =>
            latLonAltToCartesian(pt.latitude, pt.longitude, 0),
          );

          // Curved border (follows sphere curvature via SLERP)
          const borderPts = subdivideSphericalPolygon(
            cornerPoints,
            SPHERICAL_SUBDIVISIONS,
            SCENE_EARTH_RADIUS * 1.003,
          ).map((pt) => new T.Vector3(pt.x, pt.y, pt.z));
          borderPts.push(borderPts[0].clone());
          const borderGeo = new T.BufferGeometry().setFromPoints(borderPts);
          const borderMat = new T.LineBasicMaterial({
            color: highlightColor,
            transparent: true,
            opacity: 0.95,
            linewidth: 2,
          });
          conesGroupRef.current.add(new T.Line(borderGeo, borderMat));

          // Outer glow (slightly larger radius for glow effect)
          const glowPts = subdivideSphericalPolygon(
            cornerPoints,
            SPHERICAL_SUBDIVISIONS,
            SCENE_EARTH_RADIUS * 1.005,
          ).map((pt) => new T.Vector3(pt.x, pt.y, pt.z));
          glowPts.push(glowPts[0].clone());
          const glowGeo = new T.BufferGeometry().setFromPoints(glowPts);
          const glowMat = new T.LineBasicMaterial({
            color: coneColor,
            transparent: true,
            opacity: 0.4,
          });
          conesGroupRef.current.add(new T.Line(glowGeo, glowMat));

          // Cone lines from satellite to corners (lines only, no filled surface)
          if (coverage.showCone !== false) {
            for (const corner of cornerPoints) {
              const edgePt = new T.Vector3(corner.x, corner.y, corner.z)
                .normalize()
                .multiplyScalar(SCENE_EARTH_RADIUS);
              const lineGeo = new T.BufferGeometry().setFromPoints([
                satPos,
                edgePt,
              ]);
              const lineMat = new T.LineBasicMaterial({
                color: coneColor,
                transparent: true,
                opacity: 0.4,
              });
              conesGroupRef.current.add(new T.Line(lineGeo, lineMat));
            }
          }
        }

        // ====== 2. Simple cone (when no polygon, just halfAngle) ======
        else if (coverage.halfAngle && coverage.showCone !== false) {
          const halfAngleRad = coverage.halfAngle * (Math.PI / 180);
          const coneHeight = satDistance - SCENE_EARTH_RADIUS * 0.98;
          const coneRadius = coneHeight * Math.tan(halfAngleRad);

          // Cone mesh
          const coneGeo = new T.ConeGeometry(
            coneRadius,
            coneHeight,
            48,
            1,
            true,
          );
          const coneMat = new T.MeshBasicMaterial({
            color: coneColor,
            transparent: true,
            opacity: 0.12,
            side: T.DoubleSide,
            depthWrite: false,
          });
          const cone = new T.Mesh(coneGeo, coneMat);
          cone.position.copy(satPos);
          cone.lookAt(0, 0, 0);
          cone.rotateX(Math.PI / 2);
          cone.translateY(-coneHeight / 2);
          conesGroupRef.current.add(cone);

          // Footprint circle
          if (coverage.showFootprint !== false) {
            const nadirDir = satPos.clone().normalize();
            const footprintPos = nadirDir
              .clone()
              .multiplyScalar(SCENE_EARTH_RADIUS * 1.003);
            const footprintRadius =
              coneRadius * (SCENE_EARTH_RADIUS / satDistance);

            // Filled circle
            const footGeo = new T.CircleGeometry(footprintRadius, 64);
            const footMat = new T.MeshBasicMaterial({
              color: coneColor,
              transparent: true,
              opacity: 0.3,
              side: T.DoubleSide,
              depthWrite: false,
            });
            const footprint = new T.Mesh(footGeo, footMat);
            footprint.position.copy(footprintPos);
            footprint.lookAt(satPos);
            conesGroupRef.current.add(footprint);

            // Edge ring
            const ringGeo = new T.RingGeometry(
              footprintRadius * 0.97,
              footprintRadius,
              64,
            );
            const ringMat = new T.MeshBasicMaterial({
              color: highlightColor,
              transparent: true,
              opacity: 0.7,
              side: T.DoubleSide,
            });
            const ring = new T.Mesh(ringGeo, ringMat);
            ring.position.copy(footprintPos);
            ring.lookAt(satPos);
            conesGroupRef.current.add(ring);
          }
        }

        // ====== 3. Nadir line ======
        if (coverage.showNadirLine !== false) {
          let nadirGroundPoint: any;
          if (coverage.nadirPoint) {
            const np = latLonAltToCartesian(
              coverage.nadirPoint.latitude,
              coverage.nadirPoint.longitude,
              0,
            );
            nadirGroundPoint = new T.Vector3(np.x, np.y, np.z);
          } else {
            nadirGroundPoint = satPos
              .clone()
              .normalize()
              .multiplyScalar(SCENE_EARTH_RADIUS);
          }
          const lineGeo = new T.BufferGeometry().setFromPoints([
            satPos,
            nadirGroundPoint,
          ]);
          const lineMat = new T.LineBasicMaterial({
            color: highlightColor,
            transparent: true,
            opacity: 0.7,
          });
          conesGroupRef.current.add(new T.Line(lineGeo, lineMat));
        }
      });

      // ========================================
      // Legacy Region-based Visibility Cones
      // ========================================
      visibilityCones.forEach((cone) => {
        const pos = latLonAltToCartesian(
          cone.center.latitude,
          cone.center.longitude,
          0,
        );

        const coneHeight = 1000 * KM_TO_SCENE;
        const minElev = cone.minElevation || 10;
        const coneRadius =
          coneHeight * Math.tan((90 - minElev) * (Math.PI / 180) * 0.4);
        const coneColor = new T.Color(cone.color || "#22d3ee");

        const coneGeo = new T.ConeGeometry(coneRadius, coneHeight, 24, 1, true);
        const coneMat = new T.MeshBasicMaterial({
          color: coneColor,
          transparent: true,
          opacity: cone.opacity || 0.12,
          side: T.DoubleSide,
          depthWrite: false,
        });

        const coneMesh = new T.Mesh(coneGeo, coneMat);
        coneMesh.position.set(pos.x, pos.y, pos.z);
        coneMesh.lookAt(0, 0, 0);
        coneMesh.rotateX(-Math.PI / 2);
        coneMesh.translateY(coneHeight / 2);
        conesGroupRef.current.add(coneMesh);

        // Footprint ring
        const footGeo = new T.RingGeometry(
          coneRadius * 0.7,
          coneRadius * 0.9,
          32,
        );
        const footMat = new T.MeshBasicMaterial({
          color: coneColor,
          transparent: true,
          opacity: 0.3,
          side: T.DoubleSide,
        });
        const foot = new T.Mesh(footGeo, footMat);
        foot.position.set(pos.x * 1.002, pos.y * 1.002, pos.z * 1.002);
        foot.lookAt(0, 0, 0);
        conesGroupRef.current.add(foot);
      });
    }, [
      visibilityCones,
      satelliteCoverages,
      effectiveLayers.coverage,
      currentView,
    ]);

    // ==========================================================================
    // Update Communication Links
    // ==========================================================================

    const updateLinks = useCallback(() => {
      if (!THREE || !linksGroupRef.current || currentView === "solar-system")
        return;

      while (linksGroupRef.current.children.length > 0) {
        linksGroupRef.current.remove(linksGroupRef.current.children[0]);
      }

      communicationLinks.forEach((link) => {
        const fromMesh = objectMeshMapRef.current.get(link.fromId);
        const toMesh = objectMeshMapRef.current.get(link.toId);
        if (!fromMesh || !toMesh) return;

        const color =
          link.color || (link.status === "active" ? "#00ff88" : "#666666");
        const geo = new THREE.BufferGeometry().setFromPoints([
          fromMesh.position.clone(),
          toMesh.position.clone(),
        ]);

        // Dashed animated line
        const mat = new THREE.LineDashedMaterial({
          color: new THREE.Color(color),
          transparent: true,
          opacity: link.status === "active" ? 0.8 : 0.4,
          dashSize: 30,
          gapSize: 20,
        });

        const line = new THREE.Line(geo, mat);
        line.computeLineDistances(); // Required for dashed lines - must be on Line, not geometry
        line.userData = { isAnimatedLink: true };
        linksGroupRef.current.add(line);
      });
    }, [THREE, communicationLinks, currentView]);

    // ==========================================================================
    // Update Orbits
    // ==========================================================================

    const updateOrbits = useCallback(() => {
      if (!THREE || !orbitsGroupRef.current || currentView === "solar-system")
        return;

      while (orbitsGroupRef.current.children.length > 0) {
        orbitsGroupRef.current.remove(orbitsGroupRef.current.children[0]);
      }

      if (!effectiveLayers.orbits) return;

      orbitPaths.forEach((path) => {
        if (path.points.length < 2) return;
        const points = path.points.map((p) => new THREE.Vector3(p.x, p.y, p.z));
        const geo = new THREE.BufferGeometry().setFromPoints(points);
        const mat = new THREE.LineBasicMaterial({
          color: new THREE.Color(path.color || "#888888"),
          transparent: true,
          opacity: 0.5,
        });
        orbitsGroupRef.current.add(new THREE.Line(geo, mat));
      });
    }, [THREE, orbitPaths, effectiveLayers.orbits, currentView]);

    // ==========================================================================
    // Mouse Handlers
    // ==========================================================================

    const handleMouseMove = useCallback((event: MouseEvent) => {
      if (!containerRef.current || !raycasterRef.current || !cameraRef.current)
        return;

      const rect = containerRef.current.getBoundingClientRect();
      mouseRef.current.set(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1,
      );

      raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);

      const selectables: any[] = [];
      objectsGroupRef.current?.traverse((obj: any) => {
        if (obj.userData?.isSelectable) selectables.push(obj);
      });

      const intersects = raycasterRef.current.intersectObjects(
        selectables,
        true,
      );

      if (intersects.length > 0) {
        let hit = intersects[0].object;
        while (hit && !hit.userData?.objectId) hit = hit.parent;

        if (hit?.userData) {
          setHoverTooltip({
            visible: true,
            x: event.clientX - rect.left,
            y: event.clientY - rect.top,
            object: hit.userData.data,
          });
          if (containerRef.current)
            containerRef.current.style.cursor = "pointer";
          return;
        }
      }

      setHoverTooltip({ visible: false, x: 0, y: 0, object: null });
      if (containerRef.current) containerRef.current.style.cursor = "grab";
    }, []);

    const handleClick = useCallback(
      (event: MouseEvent) => {
        if (
          !containerRef.current ||
          !raycasterRef.current ||
          !cameraRef.current
        )
          return;

        const rect = containerRef.current.getBoundingClientRect();
        mouseRef.current.set(
          ((event.clientX - rect.left) / rect.width) * 2 - 1,
          -((event.clientY - rect.top) / rect.height) * 2 + 1,
        );

        raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);

        // Check objects group
        const selectables: any[] = [];
        objectsGroupRef.current?.traverse((obj: any) => {
          if (obj.userData?.isSelectable) selectables.push(obj);
        });
        // Also check planets group for solar system view
        planetsGroupRef.current?.traverse((obj: any) => {
          if (obj.userData?.isSelectable) selectables.push(obj);
        });

        const intersects = raycasterRef.current.intersectObjects(
          selectables,
          true,
        );

        if (intersects.length > 0) {
          let hit = intersects[0].object;
          while (hit && !hit.userData?.objectId) hit = hit.parent;

          if (hit?.userData?.objectId) {
            const objectId = hit.userData.objectId;
            setSelection({
              selectedId: objectId,
              selectedObject: hit.userData.data,
              hoveredId: null,
            });
            callbacks?.onSelect?.(hit.userData.data);

            // Fly to the clicked object
            flyTo(objectId, { animate: true });
            return;
          }
        }

        setSelection({
          selectedId: null,
          selectedObject: null,
          hoveredId: null,
        });
        callbacks?.onSelect?.(null);
      },
      [callbacks],
    );

    // ==========================================================================
    // Fly To Object (comprehensive camera navigation)
    // ==========================================================================

    const flyTo = useCallback(
      (
        objectId: string,
        options?: {
          zoom?: number;
          animate?: boolean;
          distance?: number;
          offset?: Vector3D;
        },
      ) => {
        if (!THREE || !cameraRef.current || !controlsRef.current) return;

        const mesh = objectMeshMapRef.current.get(objectId);
        if (!mesh) {
          if (import.meta.env.DEV) {
            console.warn(`flyTo: Object "${objectId}" not found`);
          }
          return;
        }

        const targetPos = mesh.position.clone();
        const camera = cameraRef.current;
        const controls = controlsRef.current;
        const category = mesh.userData?.category;
        const isGroundStation = category === "groundStation";

        // Determine viewing distance based on object type
        let viewDistance: number;
        if (options?.distance) {
          viewDistance = options.distance;
        } else {
          // Auto-calculate based on object size/type
          const isPlanet = mesh.userData?.isPlanet;
          const targetDist = targetPos.length();

          if (isPlanet) {
            // For planets, get closer based on planet size
            const planetRadius = mesh.geometry?.parameters?.radius || 100;
            viewDistance = planetRadius * 5;
          } else if (isGroundStation) {
            // Ground stations - close view from above
            viewDistance = 200;
          } else if (category === "station") {
            // Space stations - extremely close side view
            viewDistance = 80;
          } else if (category === "satellite") {
            // Satellites - extremely close side view
            viewDistance = 60;
          } else if (targetDist > SCENE_EARTH_RADIUS * 2) {
            // Far object (high orbit) - close view
            viewDistance = 150;
          } else {
            // Near-Earth object - very close view
            viewDistance = 100 * (options?.zoom || 1);
          }
        }

        // Calculate camera position - offset from object
        let cameraTargetPos: any;
        if (options?.offset) {
          cameraTargetPos = targetPos
            .clone()
            .add(
              new THREE.Vector3(
                options.offset.x,
                options.offset.y,
                options.offset.z,
              ),
            );
        } else if (isGroundStation) {
          // Ground station: view from above (radially outward from Earth center)
          const upDir = targetPos.clone().normalize();
          cameraTargetPos = targetPos
            .clone()
            .add(upDir.multiplyScalar(viewDistance));
        } else {
          // Satellites/stations: view from the side
          const dir = targetPos.clone().normalize();
          const side = new THREE.Vector3(-dir.z, 0.2, dir.x).normalize();
          cameraTargetPos = targetPos
            .clone()
            .add(side.multiplyScalar(viewDistance));
        }

        if (options?.animate !== false) {
          focusTransitionRef.current = {
            startTime: Date.now(),
            startPos: {
              x: camera.position.x,
              y: camera.position.y,
              z: camera.position.z,
            },
            targetPos: {
              x: cameraTargetPos.x,
              y: cameraTargetPos.y,
              z: cameraTargetPos.z,
            },
            startTarget: {
              x: controls.target.x,
              y: controls.target.y,
              z: controls.target.z,
            },
            targetTarget: { x: targetPos.x, y: targetPos.y, z: targetPos.z },
            duration: CAMERA_TRANSITION_DURATION,
            isActive: true,
          };
        } else {
          camera.position.copy(cameraTargetPos);
          controls.target.copy(targetPos);
          controls.update();
        }

        // Fire callback
        callbacks?.onFocus?.(mesh.userData?.data || { id: objectId });
      },
      [THREE, callbacks],
    );

    // Alias for backward compatibility
    const focusOn = flyTo;

    // ==========================================================================
    // Animation
    // ==========================================================================

    const animate = useCallback(() => {
      // Guard: Only one animation loop should run at a time
      if (!isAnimatingRef.current) return;
      if (!rendererRef.current || !sceneRef.current || !cameraRef.current)
        return;

      const camera = cameraRef.current;
      const controls = controlsRef.current;

      // Transition
      if (focusTransitionRef.current?.isActive) {
        const t = focusTransitionRef.current;
        const progress = Math.min((Date.now() - t.startTime) / t.duration, 1);
        const e = easeOutCubic(progress);

        camera.position.x = t.startPos.x + (t.targetPos.x - t.startPos.x) * e;
        camera.position.y = t.startPos.y + (t.targetPos.y - t.startPos.y) * e;
        camera.position.z = t.startPos.z + (t.targetPos.z - t.startPos.z) * e;

        controls.target.x =
          t.startTarget.x + (t.targetTarget.x - t.startTarget.x) * e;
        controls.target.y =
          t.startTarget.y + (t.targetTarget.y - t.startTarget.y) * e;
        controls.target.z =
          t.startTarget.z + (t.targetTarget.z - t.startTarget.z) * e;

        if (progress >= 1) t.isActive = false;
      }

      if (controls) controls.update();

      timeRef.current += 0.016;
      if (starsMaterialRef.current)
        starsMaterialRef.current.uniforms.time.value = timeRef.current;

      // Animate communication link dashes (moving dotted lines)
      if (linksGroupRef.current) {
        linksGroupRef.current.children.forEach((child: any) => {
          if (child.userData?.isAnimatedLink && child.material) {
            child.material.dashOffset -= 1.5; // Speed of animation
          }
        });
      }

      rendererRef.current.render(sceneRef.current, camera);
      animationRef.current = requestAnimationFrame(animate);
    }, []);

    // ==========================================================================
    // Imperative Handle
    // ==========================================================================

    useImperativeHandle(
      ref,
      () => ({
        focusOn,
        flyTo,
        setView: (mode: ViewMode) => setCurrentView(mode),
        setTime: () => {},
        setTimeScale: () => {},
        togglePlay: () => {},
        setLayerVisibility: (layer: keyof LayerVisibility, visible: boolean) =>
          setLayers((prev) => ({ ...prev, [layer]: visible })),
        getCameraState: (): CameraState => {
          if (!cameraRef.current)
            return {
              position: { x: 0, y: 0, z: 10000 },
              target: { x: 0, y: 0, z: 0 },
              up: { x: 0, y: 1, z: 0 },
              fov: 45,
              near: 0.1,
              far: 100000,
            };
          const c = cameraRef.current;
          return {
            position: { x: c.position.x, y: c.position.y, z: c.position.z },
            target: {
              x: controlsRef.current?.target.x || 0,
              y: controlsRef.current?.target.y || 0,
              z: controlsRef.current?.target.z || 0,
            },
            up: { x: c.up.x, y: c.up.y, z: c.up.z },
            fov: c.fov,
            near: c.near,
            far: c.far,
          };
        },
        setCameraState: (state: Partial<CameraState>) => {
          if (!cameraRef.current) return;
          if (state.position)
            cameraRef.current.position.set(
              state.position.x,
              state.position.y,
              state.position.z,
            );
          if (state.target && controlsRef.current)
            controlsRef.current.target.set(
              state.target.x,
              state.target.y,
              state.target.z,
            );
        },
        exportImage: (format: "png" | "jpeg" = "png"): string =>
          rendererRef.current?.domElement.toDataURL(`image/${format}`) || "",
        exportScene: (): Blob => new Blob(),
        getVisibleObjects: (): SpaceObject[] => [
          ...satellites,
          ...debris,
          ...stations,
          ...customObjects,
        ],
        findObjects: (q: string): SpaceObject[] =>
          [...satellites, ...debris, ...stations, ...customObjects].filter(
            (obj) =>
              obj.name.toLowerCase().includes(q.toLowerCase()) ||
              obj.id.toLowerCase().includes(q.toLowerCase()),
          ),
        addObject: () => {},
        removeObject: () => {},
        updateObject: () => {},
        measureDistance: (
          _from: string | Vector3D,
          _to: string | Vector3D,
        ): number => 0,
      }),
      [satellites, debris, stations, customObjects, focusOn],
    );

    // ==========================================================================
    // Effects
    // ==========================================================================

    useEffect(() => {
      if (THREE && OrbitControlsClass) return initScene();
    }, [THREE, OrbitControlsClass, initScene]);

    useEffect(() => {
      if (!isLoading && THREE) {
        // Cancel any existing animation loop first (prevents stacking)
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }

        // Start animation loop with guard
        isAnimatingRef.current = true;
        animate();

        return () => {
          // Stop animation loop cleanly
          isAnimatingRef.current = false;
          if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
            animationRef.current = null;
          }
        };
      }
    }, [isLoading, THREE, animate]);

    useEffect(() => {
      if (THREE && !isLoading) {
        updateObjects();
        updateGroundStations();
        updateCoverage();
        updateOrbits();
        updateLinks();
      }
    }, [
      THREE,
      isLoading,
      updateObjects,
      updateGroundStations,
      updateCoverage,
      updateOrbits,
      updateLinks,
    ]);

    useEffect(() => {
      const container = containerRef.current;
      if (!container || isLoading) return;
      container.addEventListener("mousemove", handleMouseMove);
      container.addEventListener("click", handleClick);
      return () => {
        container.removeEventListener("mousemove", handleMouseMove);
        container.removeEventListener("click", handleClick);
      };
    }, [isLoading, handleMouseMove, handleClick]);

    useEffect(() => {
      const handleResize = () => {
        if (!containerRef.current || !rendererRef.current || !cameraRef.current)
          return;
        const rect = containerRef.current.getBoundingClientRect();
        const h = typeof height === "number" ? height : rect.height;
        cameraRef.current.aspect = rect.width / h;
        cameraRef.current.updateProjectionMatrix();
        rendererRef.current.setSize(rect.width, h);
      };
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, [height]);

    useEffect(() => {
      if (focusedObjectId) focusOn(focusedObjectId, { animate: true });
    }, [focusedObjectId, focusOn]);

    // Update sun direction for atmosphere glow (from Zendir SDK)
    useEffect(() => {
      if (THREE && atmosphereMaterialRef.current && sunDirection) {
        const dir = new THREE.Vector3(
          sunDirection.x,
          sunDirection.y,
          sunDirection.z,
        ).normalize();
        atmosphereMaterialRef.current.uniforms.uSunDirection.value = dir;
      }
    }, [THREE, sunDirection]);

    // Update planet positions from Zendir SDK
    useEffect(() => {
      if (!THREE || !planetPositions || !planetsGroupRef.current) return;

      planetsGroupRef.current.traverse((child: any) => {
        if (
          child.userData?.planetId &&
          planetPositions[child.userData.planetId]
        ) {
          const pos = planetPositions[child.userData.planetId];
          child.position.set(pos.x, pos.y, pos.z);
        }
      });
    }, [THREE, planetPositions]);

    // ==========================================================================
    // Render
    // ==========================================================================

    if (!webglCapabilities.available) {
      return (
        <div
          className={`zenspace3d-fallback ${className}`}
          style={{
            width,
            height: typeof height === "number" ? height : 600,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#030508",
            borderRadius: 8,
            fontFamily: "ui-monospace, monospace",
            ...style,
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <AstroIcon
              name="public"
              size="large"
              label="Globe"
              style={{ color: "#22d3ee", opacity: 0.5 }}
            />
          </div>
          <div
            style={{
              color: "#b8bcc8" /* WCAG: improved contrast */,
              fontSize: 14,
            }}
          >
            WebGL is not available
          </div>
        </div>
      );
    }

    if (error)
      return (
        <div
          style={{
            width,
            height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#0a0a0f",
            color: "#ff4444",
          }}
        >
          <p>Error: {error}</p>
        </div>
      );

    return (
      <div
        className={`zenspace3d ${className}`}
        style={{
          width,
          height: typeof height === "number" ? height : "100%",
          position: "relative",
          backgroundColor: "#000",
          borderRadius: 8,
          overflow: "hidden",
          cursor: "grab",
          ...style,
        }}
      >
        <div ref={containerRef} style={{ width: "100%", height: "100%" }} />

        {isLoading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0,0,0,0.9)",
            }}
          >
            <div style={{ color: "#22d3ee", fontSize: 14 }}>Loading...</div>
          </div>
        )}

        {showTools && !isLoading && (
          <div
            style={{
              position: "absolute",
              top: 12,
              left: 12,
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            <LayerBtn
              icon="satellite"
              label="Satellites"
              active={layers.satellites}
              onClick={() =>
                setLayers((l) => ({ ...l, satellites: !l.satellites }))
              }
            />
            <LayerBtn
              icon="home"
              label="Stations"
              active={layers.stations}
              onClick={() =>
                setLayers((l) => ({ ...l, stations: !l.stations }))
              }
            />
            <LayerBtn
              icon="antenna"
              label="Ground"
              active={layers.groundStations}
              onClick={() =>
                setLayers((l) => ({ ...l, groundStations: !l.groundStations }))
              }
            />
            <LayerBtn
              icon="timeline"
              label="Orbits"
              active={layers.orbits}
              onClick={() => setLayers((l) => ({ ...l, orbits: !l.orbits }))}
            />
            <LayerBtn
              icon="signal-cellular-4-bar"
              label="Coverage"
              active={layers.coverage}
              onClick={() =>
                setLayers((l) => ({ ...l, coverage: !l.coverage }))
              }
            />
          </div>
        )}

        {!isLoading && (
          <ObjectLegend
            currentView={currentView}
            satellites={satellites}
            stations={stations}
            groundStations={groundStations}
            onObjectClick={(id, objectData) => {
              // Same behavior as clicking in 3D: select, callback, fly
              setSelection({
                selectedId: id,
                selectedObject: objectData,
                hoveredId: null,
              });
              callbacks?.onSelect?.(objectData);
              flyTo(id, { animate: true });
            }}
          />
        )}

        {hoverTooltip.visible && hoverTooltip.object && (
          <div
            style={{
              position: "absolute",
              left: hoverTooltip.x + 15,
              top: hoverTooltip.y - 10,
              backgroundColor: "rgba(20, 25, 35, 0.95)",
              color: "#e4e4e7",
              padding: "10px 14px",
              borderRadius: 4,
              fontSize: 12,
              fontFamily: "system-ui, -apple-system, sans-serif",
              boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
              pointerEvents: "none",
              zIndex: 100,
              minWidth: 140,
            }}
          >
            <div style={{ color: "#ffffff", fontWeight: 500, marginBottom: 4 }}>
              {hoverTooltip.object.name ||
                hoverTooltip.object.targetName ||
                "Unknown"}
            </div>
            {hoverTooltip.object.category && (
              <div style={{ color: "#9ca3af", fontSize: 11 }}>
                {hoverTooltip.object.category}
              </div>
            )}
            {hoverTooltip.object.overpassTime && (
              <div style={{ color: "#9ca3af", fontSize: 11, marginTop: 4 }}>
                Overpass time: {hoverTooltip.object.overpassTime}
              </div>
            )}
          </div>
        )}

        {/* Overpass labels removed - now shown only on hover */}

        {showInfoPanel && selection.selectedObject && (
          <div
            style={{
              position: "absolute",
              bottom: 16,
              right: 12,
              backgroundColor: "rgba(0,0,0,0.9)",
              color: "#e4e4e7",
              padding: "14px 18px",
              borderRadius: 10,
              fontSize: 12,
              fontFamily: "ui-monospace, monospace",
              border: "1px solid rgba(34,211,238,0.3)",
              minWidth: 180,
            }}
          >
            <div style={{ color: "#22d3ee", fontWeight: 500, marginBottom: 8 }}>
              {selection.selectedObject.name}
            </div>
            <div
              style={{
                color: "#b8bcc8" /* WCAG: improved contrast */,
                fontSize: 10,
              }}
            >
              {selection.selectedObject.category}
            </div>
            <button
              onClick={() =>
                setSelection({
                  selectedId: null,
                  selectedObject: null,
                  hoveredId: null,
                })
              }
              style={{
                marginTop: 10,
                padding: "5px 10px",
                backgroundColor: "rgba(255,255,255,0.1)",
                border: "none",
                borderRadius: 4,
                color: "#a1a1aa",
                cursor: "pointer",
                fontSize: 10,
                width: "100%",
              }}
            >
              Close
            </button>
          </div>
        )}

        {!isLoading && (
          <div
            style={{
              position: "absolute",
              bottom: 12,
              left: 12,
              backgroundColor: "rgba(0,0,0,0.5)",
              color: "#b8bcc8" /* WCAG: improved contrast */,
              padding: "5px 10px",
              borderRadius: 6,
              fontSize: 10,
            }}
          >
            Drag to rotate • Scroll to zoom
          </div>
        )}
      </div>
    );
  },
);

// =============================================================================
// Sub-Components
// =============================================================================

function LayerBtn({
  icon,
  label,
  active,
  onClick,
}: {
  icon: AstroIconName;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={`Toggle ${label} layer`}
      aria-pressed={active}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 10px",
        backgroundColor: active ? "rgba(34,211,238,0.15)" : "rgba(0,0,0,0.7)",
        border: active
          ? "1px solid rgba(34,211,238,0.4)"
          : "1px solid rgba(255,255,255,0.1)",
        borderRadius: 6,
        cursor: "pointer",
        fontSize: "0.6875rem", // 11px in rem
        color: active ? "#22d3ee" : "#b8bcc8", // WCAG: improved contrast from #71717a
        minWidth: 110,
      }}
    >
      <span aria-hidden="true">
        <AstroIcon name={icon} size="extra-small" label="" />
      </span>
      <span>{label}</span>
    </button>
  );
}

function _OverpassLabel({
  overpass,
  index,
}: {
  overpass: OverpassInfo;
  index: number;
}) {
  return (
    <div
      style={{
        position: "absolute",
        top: 70 + index * 60,
        left: "28%",
        backgroundColor: "rgba(0,0,0,0.85)",
        color: "#e4e4e7",
        padding: "8px 12px",
        borderRadius: 6,
        fontSize: 11,
        fontFamily: "ui-monospace, monospace",
        border: "1px solid rgba(34,211,238,0.3)",
        pointerEvents: "none",
      }}
    >
      <div style={{ fontWeight: 500, marginBottom: 2 }}>
        {overpass.targetName}
      </div>
      <div style={{ color: "#22d3ee" }}>
        Overpass: {formatDuration(overpass.timeToOverpass)}
      </div>
    </div>
  );
}

function ObjectLegend({
  currentView,
  satellites,
  stations,
  groundStations,
  onObjectClick,
}: {
  currentView: string;
  satellites: any[];
  stations: any[];
  groundStations: any[];
  onObjectClick: (id: string, objectData: any) => void;
}) {
  const [hoveredSection, setHoveredSection] = React.useState<string | null>(
    null,
  );
  const [pinnedSection, setPinnedSection] = React.useState<string | null>(null);

  // Show section if hovered OR pinned
  const isExpanded = (section: string) =>
    hoveredSection === section || pinnedSection === section;

  // Click toggles pin state
  const togglePin = (section: string) => {
    setPinnedSection((prev) => (prev === section ? null : section));
  };

  const sectionStyle: React.CSSProperties = {
    cursor: "pointer",
    padding: "4px 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    transition: "background-color 0.15s",
  };

  const itemStyle: React.CSSProperties = {
    padding: "3px 8px",
    marginLeft: 12,
    cursor: "pointer",
    borderRadius: 4,
    fontSize: 10,
    color: "#a1a1aa",
    transition: "all 0.15s",
  };

  return (
    <div
      style={{
        position: "absolute",
        top: 12,
        right: 12,
        backgroundColor: "rgba(0,0,0,0.85)",
        color: "#e4e4e7",
        padding: "10px 14px",
        borderRadius: 8,
        fontSize: 11,
        fontFamily: "ui-monospace, monospace",
        minWidth: 160,
        maxHeight: 400,
        overflowY: "auto",
      }}
    >
      <div
        style={{
          color: "#b8bcc8" /* WCAG: improved contrast */,
          fontSize: 10,
          marginBottom: 8,
        }}
      >
        {currentView === "solar-system" ? "Solar System" : "Earth Orbit"}
      </div>

      {satellites.length > 0 && (
        <div
          onMouseEnter={() => setHoveredSection("satellites")}
          onMouseLeave={() => setHoveredSection(null)}
        >
          <div
            style={{
              ...sectionStyle,
              backgroundColor: isExpanded("satellites")
                ? "rgba(255,255,255,0.05)"
                : "transparent",
            }}
            onClick={() => togglePin("satellites")}
          >
            <span>
              <span style={{ color: "#00ff88" }}>●</span> {satellites.length}{" "}
              Satellites
            </span>
            <span
              style={{
                color: pinnedSection === "satellites" ? "#00ff88" : "#666",
                fontSize: 9,
              }}
            >
              {isExpanded("satellites") ? "▼" : "▶"}
              {pinnedSection === "satellites" ? (
                <>
                  <AstroIcon
                    name="bookmark"
                    size="extra-small"
                    label=""
                    style={{ marginLeft: 4, verticalAlign: "middle" }}
                  />
                </>
              ) : null}
            </span>
          </div>
          {isExpanded("satellites") && (
            <div style={{ marginBottom: 4, paddingBottom: 4 }}>
              {satellites.map((sat) => (
                <div
                  key={sat.id}
                  style={itemStyle}
                  onClick={(e) => {
                    e.stopPropagation();
                    onObjectClick(sat.id, sat);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(0,255,136,0.15)";
                    e.currentTarget.style.color = "#00ff88";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "#a1a1aa";
                  }}
                >
                  {sat.name || sat.id}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {stations.length > 0 && (
        <div
          onMouseEnter={() => setHoveredSection("stations")}
          onMouseLeave={() => setHoveredSection(null)}
        >
          <div
            style={{
              ...sectionStyle,
              backgroundColor: isExpanded("stations")
                ? "rgba(255,255,255,0.05)"
                : "transparent",
            }}
            onClick={() => togglePin("stations")}
          >
            <span>
              <span style={{ color: "#ffd700" }}>●</span> {stations.length}{" "}
              Stations
            </span>
            <span
              style={{
                color: pinnedSection === "stations" ? "#ffd700" : "#666",
                fontSize: 9,
              }}
            >
              {isExpanded("stations") ? "▼" : "▶"}
              {pinnedSection === "stations" ? (
                <>
                  <AstroIcon
                    name="bookmark"
                    size="extra-small"
                    label=""
                    style={{ marginLeft: 4, verticalAlign: "middle" }}
                  />
                </>
              ) : null}
            </span>
          </div>
          {isExpanded("stations") && (
            <div style={{ marginBottom: 4, paddingBottom: 4 }}>
              {stations.map((st) => (
                <div
                  key={st.id}
                  style={itemStyle}
                  onClick={(e) => {
                    e.stopPropagation();
                    onObjectClick(st.id, st);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(255,215,0,0.15)";
                    e.currentTarget.style.color = "#ffd700";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "#a1a1aa";
                  }}
                >
                  {st.name || st.id}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {groundStations.length > 0 && (
        <div
          onMouseEnter={() => setHoveredSection("groundStations")}
          onMouseLeave={() => setHoveredSection(null)}
        >
          <div
            style={{
              ...sectionStyle,
              backgroundColor: isExpanded("groundStations")
                ? "rgba(255,255,255,0.05)"
                : "transparent",
            }}
            onClick={() => togglePin("groundStations")}
          >
            <span>
              <span style={{ color: "#00ffff" }}>▲</span>{" "}
              {groundStations.length} Ground Stations
            </span>
            <span
              style={{
                color: pinnedSection === "groundStations" ? "#00ffff" : "#666",
                fontSize: 9,
              }}
            >
              {isExpanded("groundStations") ? "▼" : "▶"}
              {pinnedSection === "groundStations" ? (
                <>
                  <AstroIcon
                    name="bookmark"
                    size="extra-small"
                    label=""
                    style={{ marginLeft: 4, verticalAlign: "middle" }}
                  />
                </>
              ) : null}
            </span>
          </div>
          {isExpanded("groundStations") && (
            <div style={{ marginBottom: 4, paddingBottom: 4 }}>
              {groundStations.map((gs) => (
                <div
                  key={gs.id}
                  style={itemStyle}
                  onClick={(e) => {
                    e.stopPropagation();
                    onObjectClick(gs.id, gs);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(0,255,255,0.15)";
                    e.currentTarget.style.color = "#00ffff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "#a1a1aa";
                  }}
                >
                  {gs.name || gs.id}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ZenSpace3D;
